import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { Suspense, useMemo, useRef, useState } from 'react'
import { ApplicationsBreadcrumb } from '../ApplicationsBreadcrumbs'
import { DataTableLoading } from '@/catalog-apps/components/component-library/DataTableComponent/DataTableLoading'
import { TablePageContainer } from '@/components/ui/DataTable/PageTableContainer'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { transformSharedFilesToTree } from './applicationFileTree'
import { ApplicationFileTreeView } from './ApplicationFileTreeView'
import { useCurrentApplication } from '../useCurrentApplication'
import { useListSharedFiles } from './useListSharedFiles'
import { Button } from '@/components/shadcn/button'
import { IconV2UploadOutline } from '@icons/IconV2UploadOutline'
import { IconV2FolderOutline } from '@icons/IconV2FolderOutline'
import { Input } from '@/components/shadcn/input'
import { toast } from 'sonner'
import { ENDPOINTS } from '@/util'

export const ApplicationFileManagerPage = () => {
  return (
    <>
      <ApplicationsBreadcrumb title="File Manager" />
      <QueryErrorBoundary errorMessage="Failed to load application shared files">
        <Suspense fallback={<DataTableLoading />}>
          <ApplicationFileManagerPageContent />
        </Suspense>
      </QueryErrorBoundary>
    </>
  )
}

function ApplicationFileManagerPageContent() {
  const { appKey } = useCurrentApplication()
  const { data, refetch } = useListSharedFiles(appKey)

  const files = data?.files ?? []
  const fileTree = useMemo(() => transformSharedFilesToTree(files), [files])

  const handleRefresh = () => {
    void refetch()
  }

  return (
    <TablePageContainer className="flex min-h-0 flex-col gap-4">
      <ApplicationFileManagerToolbar appKey={appKey} onRefresh={handleRefresh} />
      <ScrollArea className="min-h-0 flex-1 pr-3">
        <ApplicationFileTreeView className="py-2 pr-1" nodes={fileTree} appKey={appKey} onRefresh={handleRefresh} />
      </ScrollArea>
    </TablePageContainer>
  )
}

function ApplicationFileManagerToolbar({ appKey, onRefresh }: { appKey: string; onRefresh: () => void }) {
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isCreatingFolder, setIsCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [isCreatingFolderPending, setIsCreatingFolderPending] = useState(false)

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setIsUploading(true)
    const token = JSON.parse(sessionStorage.getItem('token') ?? '')
    try {
      const formData = new FormData()
      formData.append('appKey', appKey)
      formData.append('namespace', '')

      if (files.length === 1) {
        formData.append('file', files[0])
        const response = await fetch(`${ENDPOINTS.LANDING}/storage/upload_shared_file/`, {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token },
          body: formData,
        })
        if (!response.ok) throw new Error(response.statusText)
        toast.success('File uploaded.')
      } else {
        Array.from(files).forEach((file, i) => formData.append(`file${i + 1}`, file))
        const response = await fetch(`${ENDPOINTS.LANDING}/storage/upload_shared_files/`, {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token },
          body: formData,
        })
        if (!response.ok) throw new Error(response.statusText)
        toast.success(`${files.length} files uploaded.`)
      }
      onRefresh()
    } catch {
      toast.error('Upload failed.')
    } finally {
      setIsUploading(false)
      if (uploadInputRef.current) uploadInputRef.current.value = ''
    }
  }

  const handleCreateFolder = async () => {
    const name = newFolderName.trim()
    if (!name) return

    setIsCreatingFolderPending(true)
    const token = JSON.parse(sessionStorage.getItem('token') ?? '')
    try {
      const response = await fetch(`${ENDPOINTS.LANDING}/storage/create_shared_directory/`, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ appKey, namespace: name }),
      })
      if (!response.ok) throw new Error(response.statusText)
      toast.success(`Folder "${name}" created.`)
      setNewFolderName('')
      setIsCreatingFolder(false)
      onRefresh()
    } catch {
      toast.error('Failed to create folder.')
    } finally {
      setIsCreatingFolderPending(false)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 pt-2">
      <input ref={uploadInputRef} type="file" multiple className="hidden" onChange={handleUpload} />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={isUploading}
        onClick={() => uploadInputRef.current?.click()}
      >
        <IconV2UploadOutline className="size-4" />
        {isUploading ? 'Uploading…' : 'Upload files'}
      </Button>
      {isCreatingFolder ? (
        <div className="flex items-center gap-2">
          <Input
            autoFocus
            placeholder="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolder()
              if (e.key === 'Escape') {
                setIsCreatingFolder(false)
                setNewFolderName('')
              }
            }}
            className="h-8 w-40 text-sm"
          />
          <Button
            type="button"
            size="sm"
            disabled={!newFolderName.trim() || isCreatingFolderPending}
            onClick={handleCreateFolder}
          >
            {isCreatingFolderPending ? 'Creating…' : 'Create'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setIsCreatingFolder(false)
              setNewFolderName('')
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setIsCreatingFolder(true)}>
          <IconV2FolderOutline className="size-4" />
          New folder
        </Button>
      )}
    </div>
  )
}
